import { useCallback, useEffect, useMemo, useState } from "react";
import { isHttpError } from "../api/http";
import { useApi } from "../api/api";
import {
  AlertBanner,
  PageTitle,
  Panel,
  StatusChip,
  YamlBlock,
  btnPrimary,
  inputClass,
  selectClass,
} from "../api/controlPlaneUi";
import {
  ModelCatalogEntry,
  Run,
  STAGES,
  STAGE_LABEL,
  Stage,
  StageConfig,
  SystemReview,
} from "../api/types";

// --- Helpers ---------------------------------------------------------------

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

const shortId = (id: string): string => (id.length > 8 ? id.slice(0, 8) : id);

// datetime-local value (local wall-clock, no zone) -> ISO-8601 UTC.
const toIso = (local: string): string =>
  local ? new Date(local).toISOString() : "";

// Human-readable summary of the filters a review was scoped to.
function filterSummary(
  review: SystemReview,
  runs: Run[]
): string {
  const parts: string[] = [];
  if (review.runId) {
    const run = runs.find((r) => r.id === review.runId);
    parts.push(
      `run: ${run ? run.title || run.storySlug || shortId(run.id) : shortId(review.runId)}`
    );
  }
  if (review.stage) {
    const label = STAGE_LABEL[review.stage as Stage] ?? review.stage;
    parts.push(`stage: ${label}`);
  }
  if (review.model) parts.push(`model: ${review.model}`);
  if (review.from) parts.push(`from: ${formatDate(review.from)}`);
  if (review.to) parts.push(`to: ${formatDate(review.to)}`);
  return parts.length ? parts.join(" · ") : "All logs & config (no filters)";
}

// --- Page ------------------------------------------------------------------

const ReviewPage: React.FC = () => {
  const api = useApi();

  // Reference data for the dropdowns.
  const [runs, setRuns] = useState<Run[]>([]);
  const [models, setModels] = useState<ModelCatalogEntry[]>([]);
  const [config, setConfig] = useState<StageConfig[]>([]);

  // Past reviews + selection.
  const [reviews, setReviews] = useState<SystemReview[]>([]);
  const [selected, setSelected] = useState<SystemReview | null>(null);

  // Request form state.
  const [runId, setRunId] = useState("");
  const [stage, setStage] = useState("");
  const [model, setModel] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [reviewModel, setReviewModel] = useState("");

  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The analyze stage's model is the default reviewer (frontier model).
  const defaultReviewModel = useMemo(
    () => config.find((c) => c.stage === "analyze")?.model ?? "",
    [config]
  );

  const loadReviews = useCallback(async () => {
    try {
      const list = await api.getSystemReviews();
      // Newest first (defensive — server is expected to already sort).
      const sorted = [...list].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setReviews(sorted);
      return sorted;
    } catch {
      // Reviews list failing to load shouldn't blank the whole page.
      return [] as SystemReview[];
    }
  }, [api]);

  // Initial load of reference data + past reviews.
  useEffect(() => {
    let active = true;
    (async () => {
      const [runsRes, modelsRes, configRes] = await Promise.all([
        api.getRuns().catch(() => [] as Run[]),
        api.getModels().catch(() => [] as ModelCatalogEntry[]),
        api.getConfig().catch(() => [] as StageConfig[]),
      ]);
      if (!active) return;
      setRuns(runsRes);
      setModels(modelsRes);
      setConfig(configRes);
      await loadReviews();
    })();
    return () => {
      active = false;
    };
  }, [api, loadReviews]);

  const modelOptions = useMemo(
    () =>
      Array.from(new Set(models.map((m) => m.name).filter(Boolean))).sort(),
    [models]
  );

  const runReview = async () => {
    setRunning(true);
    setError(null);
    try {
      // Omit empty fields so they aren't sent as filters.
      const body: {
        runId?: string;
        stage?: string;
        model?: string;
        from?: string;
        to?: string;
        reviewModel?: string;
      } = {};
      if (runId) body.runId = runId;
      if (stage) body.stage = stage;
      if (model) body.model = model;
      const fromIso = toIso(from);
      const toIsoVal = toIso(to);
      if (fromIso) body.from = fromIso;
      if (toIsoVal) body.to = toIsoVal;
      // Only send reviewModel if the user explicitly picked one; otherwise let
      // the control plane fall back to the analyze stage's model.
      if (reviewModel) body.reviewModel = reviewModel;

      const created = await api.createSystemReview(body);
      setSelected(created);
      // Refresh the list so the new review shows in the sidebar.
      await loadReviews();
    } catch (err: unknown) {
      if (isHttpError(err) && err.status === 401) {
        setError("You must be signed in to run a review.");
      } else {
        const msg =
          err instanceof Error
            ? err.message
            : "Failed to run review. Is the control plane running?";
        setError(msg);
      }
    } finally {
      setRunning(false);
    }
  };

  const effectiveReviewModel = reviewModel || defaultReviewModel;

  const labelClass =
    "block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1";

  return (
    <div className="space-y-4 max-w-[1400px]">
      <PageTitle kind="Analysis" name="system-reviews" />

      {error && <AlertBanner variant="error">{error}</AlertBanner>}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-4">
          <Panel title="New review request">
            <div className="grid gap-3 md:grid-cols-2">
              <label className="block">
                <span className={labelClass}>Run</span>
                <select
                  value={runId}
                  onChange={(e) => setRunId(e.target.value)}
                  className={selectClass}
                >
                  <option value="">all</option>
                  {runs.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.title || r.storySlug || shortId(r.id)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className={labelClass}>Stage</span>
                <select
                  value={stage}
                  onChange={(e) => setStage(e.target.value)}
                  className={selectClass}
                >
                  <option value="">all</option>
                  {STAGES.map((s) => (
                    <option key={s} value={s}>
                      {STAGE_LABEL[s]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className={labelClass}>Model filter</span>
                <select
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  className={selectClass}
                >
                  <option value="">all</option>
                  {modelOptions.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className={labelClass}>Reviewer model</span>
                <select
                  value={reviewModel}
                  onChange={(e) => setReviewModel(e.target.value)}
                  className={selectClass}
                >
                  <option value="">
                    default ({defaultReviewModel || "analyze stage"})
                  </option>
                  {modelOptions.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className={labelClass}>From</span>
                <input
                  type="datetime-local"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  className={inputClass}
                />
              </label>
              <label className="block">
                <span className={labelClass}>To</span>
                <input
                  type="datetime-local"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  className={inputClass}
                />
              </label>
            </div>
            <div className="mt-4 flex items-center gap-3">
              <button
                type="button"
                onClick={runReview}
                disabled={running}
                className={btnPrimary}
              >
                {running ? "Running…" : "Submit review"}
              </button>
              {effectiveReviewModel && (
                <span className="text-xs text-slate-500 font-mono">
                  reviewer: {effectiveReviewModel}
                </span>
              )}
            </div>
            <p className="mt-3 text-xs text-slate-500">
              Requires write auth. Without <code className="font-mono">ANTHROPIC_API_KEY</code>{" "}
              on the control plane, returns a simulated analysis.
            </p>
          </Panel>

          <Panel title="Output">
            {selected ? (
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  <StatusChip status={selected.status} />
                  <span className="font-mono text-slate-600">
                    {selected.reviewModel}
                  </span>
                  <span className="text-slate-400 font-mono text-xs">
                    {formatDate(selected.createdAt)}
                  </span>
                  {selected.requestedBy && (
                    <span className="text-slate-400 font-mono text-xs">
                      · by {selected.requestedBy}
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 font-mono">
                  scope: {filterSummary(selected, runs)}
                </p>
                {selected.status === "failed" && selected.error && (
                  <AlertBanner variant="error">{selected.error}</AlertBanner>
                )}
                {selected.result ? (
                  <YamlBlock>{selected.result}</YamlBlock>
                ) : selected.status !== "failed" ? (
                  <p className="text-sm text-slate-500 font-mono">No output.</p>
                ) : null}
              </div>
            ) : (
              <p className="text-sm text-slate-500 font-mono">
                Submit a review or select from history.
              </p>
            )}
          </Panel>
        </div>

        <Panel title={`History (${reviews.length})`}>
          <div className="space-y-1 -mx-4 -mb-4">
            {reviews.length === 0 ? (
              <p className="px-4 pb-4 text-sm text-slate-500 font-mono">
                No reviews yet.
              </p>
            ) : (
              reviews.map((r) => {
                const active = selected?.id === r.id;
                return (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setSelected(r)}
                    className={`w-full text-left px-4 py-2.5 border-l-2 transition-colors ${
                      active
                        ? "border-[#C9A260] bg-[#C9A260]/10"
                        : "border-transparent hover:bg-white/4"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <StatusChip status={r.status} />
                      <span className="text-[10px] text-slate-400 font-mono">
                        {shortId(r.id)}
                      </span>
                    </div>
                    <p className="text-[10px] text-slate-500 font-mono mt-1 truncate">
                      {filterSummary(r, runs)}
                    </p>
                  </button>
                );
              })
            )}
          </div>
        </Panel>
      </div>
    </div>
  );
};

export default ReviewPage;
